import { Component, OnInit, inject, Input, ViewChild } from '@angular/core';
import { IPolymorphicControlMeta, PolymorphicControlComponent, PolymorphicControlService, PolyMorphicControlTypes } from '@optima-stride-ai/optima-components';
import { IFieldMeta, FieldTypes, IBizEntity, IBizEntityType } from '@optima-stride-ai/core';
import { ButtonDirective, FormCheckComponent, FormCheckInputDirective, FormCheckLabelDirective, FormControlDirective, FormLabelDirective } from '@coreui/angular';
import { IconDirective } from '@coreui/icons-angular';
import { explorerProperties, Explorers, IExplorerProperties } from '../modal/modal';
import { FormsModule } from '@angular/forms';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeCategoriesNotAvailableForSelection } from '../strategy/biz-entity-category-filters';


@Component({
  selector: 'app-form-fields',
  imports: [
    PolymorphicControlComponent,
    FormCheckComponent,
    FormCheckInputDirective,
    FormCheckLabelDirective,
    IconDirective,
    ButtonDirective,
    FormsModule,
    FormControlDirective,
    FormLabelDirective
  ],
  templateUrl: './form-fields.component.html',
  styleUrl: './form-fields.component.sass',
  standalone: true,
})
export class FormFieldsComponent implements OnInit {
  @Input() fieldData!: IFieldMeta | IFieldMeta[];
  @Input() isEditing: boolean = false;
  @Input() existingFields: IFieldMeta[] = [];

  @ViewChild('nameControl') nameControl!: PolymorphicControlComponent;
  @ViewChild('typeControl') typeControl!: PolymorphicControlComponent;
  @ViewChild('labelControl') labelControl!: PolymorphicControlComponent;
  @ViewChild('bizEntityExplorerControl') bizEntityExplorerControl!: PolymorphicControlComponent;
  @ViewChild('isFilterableControl') isFilterableControl!: PolymorphicControlComponent;
  @ViewChild('isSearchableControl') isSearchableControl!: PolymorphicControlComponent;
  @ViewChild('isRequiredControl') isRequiredControl!: PolymorphicControlComponent;
  @ViewChild('viewInListControl') viewInListControl!: PolymorphicControlComponent;
  @ViewChild('hiddenInFormControl') hiddenInFormControl!: PolymorphicControlComponent;
  @ViewChild('minControl') minControl!: PolymorphicControlComponent;
  @ViewChild('maxControl') maxControl!: PolymorphicControlComponent;
  @ViewChild('inputPatternControl') inputPatternControl!: PolymorphicControlComponent;

  private _field!: IFieldMeta;
  
  // Duplicate field name error tracking
  isDuplicateFieldName: boolean = false;
  
  // BizEntity types fetch state
  isBizEntityTypesLoading: boolean = false;
  bizEntityTypesLoadError: boolean = false;
  
  // Store original bizEntityExplorerId to preserve it during async operations
  private originalBizEntityExplorerId: number | undefined;
  
  // Validation messages management 
  // validationMessages: { key: string; value: string }[] = [];
  // newValidationKey: string = '';
  // newValidationValue: string = '';
  // editingValidationIndex: number | null = null;
  // editingValidationKey: string = '';
  // editingValidationValue: string = '';
  
  polyNameControl!: IPolymorphicControlMeta;
  polyTypeControl!: IPolymorphicControlMeta;
  polyLabelControl!: IPolymorphicControlMeta;
  polyBizEntityExplorerControl!: IPolymorphicControlMeta;
  polyIsFilterableControl!: IPolymorphicControlMeta;
  polyIsSearchableControl!: IPolymorphicControlMeta;
  polyIsRequiredControl!: IPolymorphicControlMeta;
  polyViewInListControl!: IPolymorphicControlMeta;
  polyHiddenInFormControl!: IPolymorphicControlMeta;
  polyMinControl!: IPolymorphicControlMeta;
  polyMaxControl!: IPolymorphicControlMeta;
  polyInputPatternControl!: IPolymorphicControlMeta;

  showBizEntityExplorer: boolean = false;

  private readonly polymorphicControlService = inject(PolymorphicControlService);
  private readonly bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private readonly fieldTypes = this.generateFieldTypesArray();
  private bizEntityTypeList : IBizEntityType[] | undefined;
  private generateFieldTypesArray(): { id: string; label: string; icon: string }[] {
    const iconMap: { [key in FieldTypes]?: string } = {
      [FieldTypes.text]: 'cilText',
      [FieldTypes.number]: 'cilCalculator',
      [FieldTypes.date]: 'cilCalendar',
      [FieldTypes.currency]: 'cilDollar',
      [FieldTypes.rangeNumber]: 'cilEthernet',
      [FieldTypes.rangeDate]: 'cilCalendar',
      [FieldTypes.boolean]: 'cilCheckCircle',
      [FieldTypes.dataValidationList]: 'cilList',
      [FieldTypes.bizEntity]: 'cilCompass',
      [FieldTypes.calculatedColumn]: 'cilCalculator',
      [FieldTypes.component]: 'cilApplicationsSettings',
      [FieldTypes.markdown]: 'cilShortText'
    };

    return Object.keys(FieldTypes)
      .filter(key => isNaN(Number(key))) // Filter out enum numeric keys
      .map(key => {
        const enumValue = FieldTypes[key as keyof typeof FieldTypes];
        return {
          id: enumValue,
          label: FieldTypes[key as keyof typeof FieldTypes], // Use formatted label or fallback to key
          icon: iconMap[enumValue] || 'cilText'
        };
      });
  }

  get explorerTypeList(): IExplorerProperties[] {
    return explorerProperties;
  }

  ngOnInit(): void {
    // Handle array input (extract first element if array)
    this._field = Array.isArray(this.fieldData) ? this.fieldData[0] : this.fieldData;
    
    // Store original bizEntityExplorerId before any modifications (data getter may overwrite _field)
    this.originalBizEntityExplorerId = this._field?.bizEntityExplorerId;
    
    if (!this.isEditing) {
      this._field = {
        name: '',
        type: FieldTypes.text,
        label: '',
        isFilterable: true,
        isSearchable: true,
        isRequired: false,
        viewInList: true,
        icon: '',
        hiddenInForm: false,
        min: undefined,
        max: undefined,
        validationMessages: [],
        inputPattern: undefined
      };
    }

    // Initialize validation messages
    // this.validationMessages = this._field.validationMessages || [];

    // Check if field type is bizEntity to show explorer
    this.showBizEntityExplorer = this._field.type === FieldTypes.bizEntity;
    this.initializePolymorphicControls();
  }

  private initializePolymorphicControls(): void {
    this.polyNameControl = this.polymorphicControlService.getC({
      id: 'name',
      type: PolyMorphicControlTypes.text,
      label: 'Field Name',
      icon: 'cilText',
      value: this._field.name || '',
      placeholder: 'Enter field name',
      disabled: this.isEditing,
      fieldMeta: {
        id: 'name',
        name: 'Name',
        type: FieldTypes.text,
        label: 'Name Field',
        isRequired: true,
        inputPattern: '^\\S{2,160}$'
      },
      customValidationMessages: {
        pattern: "Name must be 2-160 characters with no spaces allowed.",
      }, 
    });

    this.polyTypeControl = this.polymorphicControlService.getC({
      id: 'type',
      type: PolyMorphicControlTypes.options,
      label: 'Field Type',
      icon: 'cilSquare',
      value: this._field.type || FieldTypes.text,
      optionSelected: this._field.type || FieldTypes.text,
      optionsWithIcon: this.fieldTypes,
      optionHeader: 'Select Field Type',
      disabled: this.isEditing,
      fieldMeta: {
        id: 'type',
        name: 'Type',
        type: FieldTypes.text,
        label: 'Type Field',
        isRequired: true,
      },
      // Add callback to handle type changes
      optionChangeCallback: (selectedOption: any) => {
        this.onFieldTypeChanged(selectedOption.id);
      }
    });

    this.polyLabelControl = this.polymorphicControlService.getC({
      id: 'label',
      type: PolyMorphicControlTypes.text,
      label: 'Field Label',
      icon: 'cilText',
      value: this._field.label || '',
      placeholder: 'Enter field label',
      fieldMeta: {
        id: 'label',
        name: 'Label',
        type: FieldTypes.text,
        label: 'Label Field',
        isRequired: true,
        inputPattern: '^\\s*\\S.{0,158}\\S\\s*$|^\\s*\\S{2}\\s*$'
      },
      customValidationMessages: {
        pattern: "Label must be 2-160 characters. Leading and trailing spaces are not allowed.",
      },
    });

    // Initialize BizEntity Explorer Control - starts disabled while loading
    this.polyBizEntityExplorerControl = this.polymorphicControlService.getC({
      id: 'bizEntityExplorerId',
      type: PolyMorphicControlTypes.options,
      label: 'BizEntity Type',
      icon: 'cilCompass',
      optionsWithIcon: [],
      optionHeader: 'Loading BizEntity Types...',
      disabled: true,
      optionChangeCallback: (selectedOption: any) => {
        this.onExplorerTypeChanged(selectedOption);
      }
    });

    // Fetch BizEntity types and update control once loaded
    this.loadBizEntityTypes();

    this.polyMinControl = this.polymorphicControlService.getC({
      id: 'min',
      type: PolyMorphicControlTypes.number,
      label: 'Minimum Value (Optional)',
      icon: 'cilArrowBottom',
      value: this._field.min,
      placeholder: 'Enter minimum value'
    });

    this.polyMaxControl = this.polymorphicControlService.getC({
      id: 'max',
      type: PolyMorphicControlTypes.number,
      label: 'Maximum Value (Optional)',
      icon: 'cilArrowTop',
      value: this._field.max,
      placeholder: 'Enter maximum value'
    });

    this.polyInputPatternControl = this.polymorphicControlService.getC({
      id: 'inputPattern',
      type: PolyMorphicControlTypes.text,
      label: 'Input Pattern (Optional)',
      icon: 'cilCode',
      value: this._field.inputPattern,
      placeholder: 'Enter regex pattern (e.g., .*)'
    });

    this.polyIsFilterableControl = this.polymorphicControlService.getC({
      id: 'isFilterable',
      type: PolyMorphicControlTypes.checkBox,
      label: 'Participates in Filters',
      icon: 'cilFilter',
      value: this._field.isFilterable || false
    });

    this.polyIsSearchableControl = this.polymorphicControlService.getC({
      id: 'isSearchable',
      type: PolyMorphicControlTypes.checkBox,
      label: 'Searchable',
      icon: 'cilSearch',
      value: this._field.isSearchable || false
    });

    this.polyIsRequiredControl = this.polymorphicControlService.getC({
      id: 'isRequired',
      type: PolyMorphicControlTypes.checkBox,
      label: 'Required',
      icon: 'cilCheckCircle',
      value: this._field.isRequired || false
    });

    this.polyViewInListControl = this.polymorphicControlService.getC({
      id: 'viewInList',
      type: PolyMorphicControlTypes.checkBox,
      label: 'View in List',
      icon: 'cilList',
      value: this._field.viewInList || false
    });

    this.polyHiddenInFormControl = this.polymorphicControlService.getC({
      id: 'hiddenInForm',
      type: PolyMorphicControlTypes.checkBox,
      label: 'Hidden in Form',
      icon: 'cilX',
      value: this._field.hiddenInForm || false
    });
  }

  private onFieldTypeChanged(fieldType: string): void {
    // Update the internal field type
    this._field.type = fieldType as FieldTypes;
    
    // Show/hide bizEntity explorer based on selected type
    this.showBizEntityExplorer = fieldType === FieldTypes.bizEntity;
    
    // Clear bizEntityExplorerId if type is not bizEntity
    if (fieldType !== FieldTypes.bizEntity) {
      this._field.bizEntityExplorerId = undefined;
    }
    
    // Update icon based on field type
    this._field.icon = this.getIconForFieldMeta(fieldType as FieldTypes);
  }

  private onExplorerTypeChanged(selectedOption: any): void {
    // Update the bizEntityExplorerId when explorer type changes
    // this._field.bizEntityExplorerId = Number(selectedOption.id) as Explorers;
    this.polyBizEntityExplorerControl.optionSelected = selectedOption.label; // Update displayed value
    this.polyBizEntityExplorerControl.value = selectedOption.id; // Update underlying value
    this.polyBizEntityExplorerControl.explorerSelected = this.polyBizEntityExplorerControl.value; // Store the entire selected option for later use
  }

  private checkDuplicateFieldName(field: IFieldMeta): void {
    if (this.polyNameControl.dirty && this.existingFields && this.existingFields.length > 0) {
      const normalizedName = field?.name?.toLowerCase().trim();
      this.isDuplicateFieldName = this.existingFields.some(
        existingField => existingField.name.toLowerCase().trim() === normalizedName && field !== existingField
      );
    } else {
      this.isDuplicateFieldName = false;
    }
  }

  // Validation messages methods
  // addValidationMessage(): void {
  //   if (this.newValidationKey.trim() && this.newValidationValue.trim()) {
  //     this.validationMessages.push({
  //       key: this.newValidationKey.trim(),
  //       value: this.newValidationValue.trim()
  //     });
  //     this.newValidationKey = '';
  //     this.newValidationValue = '';
  //   }
  // }

  // removeValidationMessage(index: number): void {
  //   // Cancel editing if we're deleting the message being edited
  //   if (this.editingValidationIndex === index) {
  //     this.cancelEditValidationMessage();
  //   }
  //   this.validationMessages.splice(index, 1);
  // }

  // startEditValidationMessage(index: number): void {
  //   this.editingValidationIndex = index;
  //   this.editingValidationKey = this.validationMessages[index].key;
  //   this.editingValidationValue = this.validationMessages[index].value;
  // }

  // saveEditValidationMessage(): void {
  //   if (this.editingValidationIndex !== null && 
  //       this.editingValidationKey.trim() && 
  //       this.editingValidationValue.trim()) {
  //     this.validationMessages[this.editingValidationIndex] = {
  //       key: this.editingValidationKey.trim(),
  //       value: this.editingValidationValue.trim()
  //     };
  //     this.cancelEditValidationMessage();
  //   }
  // }

  // cancelEditValidationMessage(): void {
  //   this.editingValidationIndex = null;
  //   this.editingValidationKey = '';
  //   this.editingValidationValue = '';
  // }

  // isEditingValidationMessage(index: number): boolean {
  //   return this.editingValidationIndex === index;
  // }

  // Data getter to expose field data for modal's getData pattern
  get data(): {data: IFieldMeta, isFormFieldsValid: boolean} {
    // Update internal field with latest values from controls
    this._field.name = this.polyNameControl?.value?.toLowerCase() || '';
    // Check for duplicate field names on every get data() call
    this.checkDuplicateFieldName(this._field);
    
    this._field.icon = this.getIconForFieldMeta(this._field.type);
    this._field.label = this.polyLabelControl.value || '';
    this._field.isFilterable = Boolean(this.polyIsFilterableControl.value);
    this._field.isSearchable = Boolean(this.polyIsSearchableControl.value);
    this._field.isRequired = Boolean(this.polyIsRequiredControl.value);
    this._field.viewInList = Boolean(this.polyViewInListControl.value);
    this._field.hiddenInForm = Boolean(this.polyHiddenInFormControl.value);
        
    // Parse numeric values
    this._field.min = this.polyMinControl.value ? Number(this.polyMinControl.value) : undefined;
    this._field.max = this.polyMaxControl.value ? Number(this.polyMaxControl.value) : undefined;
    
    // Input pattern
    this._field.inputPattern = this.polyInputPatternControl.value;

    // Validation messages
  //  this._field.validationMessages = this.validationMessages;

    // Include bizEntityExplorerId if type is bizEntity
    if (this._field.type === FieldTypes.bizEntity && this.polyBizEntityExplorerControl) {
      // Legacy approach: resolved explorer from explorerTypeList by label
      // const selectedExplorerLabel = this.polyBizEntityExplorerControl.optionSelected;
      // const selectedExplorer = this.explorerTypeList.find(e => e.name === selectedExplorerLabel);
      // this._field.bizEntityExplorerId = selectedExplorer?.explorer || Explorers.Strategy;
      // Now using bizEntityTypeList: value is the BizEntityType id directly
      // Only update if control has a valid value (not empty/loading)
      const controlValue = this.polyBizEntityExplorerControl.value;
      if (controlValue !== undefined && controlValue !== null && controlValue !== '') {
        this._field.bizEntityExplorerId = Number(controlValue);
      }
      // Otherwise, preserve the existing bizEntityExplorerId value
    } else{
      this._field.bizEntityExplorerId = undefined;
    }
    return {data: this._field, isFormFieldsValid: this.isFormFieldMetaValid(this._field)};
  }

  private isFormFieldMetaValid(fieldData: IFieldMeta): boolean {
    // Check required fields
    if (!fieldData.name || fieldData.name.trim() === '') {
      return false;
    }

    if (!fieldData.label || fieldData.label.trim() === '') {
      return false;
    }

    // name must be 2-160 characters, no spaces allowed
    const namePattern = /^\S{2,160}$/;
    if (!namePattern.test(fieldData.name)) {
      return false;
    }

    // label must be 2-50 characters, no leading/trailing spaces
    const labelPattern = /^\S.{0,158}\S$|^\S{2}$/;
    if (!labelPattern.test(fieldData.label)) {
      return false;
    }

    // Validate min/max for number fields
    if (fieldData.type === FieldTypes.number) {
      // Only validate max >= min when both are defined; each is independently optional
      if (fieldData.min !== undefined && fieldData.min !== null &&
          fieldData.max !== undefined && fieldData.max !== null) {
        if (fieldData.max < fieldData.min) {
          return false;
        }
      }
    }

    // Check for duplicate field names
    if (this.isDuplicateFieldName) {
      return false;
    }

    return true;
  }

  getIconForFieldMeta(fieldType: FieldTypes): string {
    switch (fieldType) {
      case FieldTypes.text:
        return "cilText";
      case FieldTypes.number:
        return "cilCalculator";
      case FieldTypes.date:
        return "cilCalendar";
      case FieldTypes.currency:
        return "cilDollar";
      case FieldTypes.rangeNumber:
        return "cilEthernet";
      case FieldTypes.rangeDate:
        return "cilCalendar";
      case FieldTypes.boolean:
        return "cilCheckCircle";
      case FieldTypes.dataValidationList:
        return "cilList";
      case FieldTypes.bizEntity:
        return "cilCompass";
      case FieldTypes.calculatedColumn:
        return "cilCalculator";
      case FieldTypes.component:
        return "cilApplicationsSettings";
      case FieldTypes.markdown:
        return "cilShortText";
      default:
        return "cilText";
    }
  }

  /**
   * Loads BizEntity types asynchronously and updates the explorer control.
   * Control remains disabled if fetch fails or while loading.
   */
  private loadBizEntityTypes(ids?: string[]): void {
    this.isBizEntityTypesLoading = true;
    this.bizEntityTypesLoadError = false;

    this.fetchBizEntityTypes(ids)
      .then(types => {
         // Filter out hidden system categories and system basic types
        const filteredTypes = types?.filter(t => !BizEntityTypeCategoriesNotAvailableForSelection.includes(t.category) && !t.isSystemBasic) || [];
        this.bizEntityTypeList = filteredTypes;
        this.isBizEntityTypesLoading = false;

        if (filteredTypes && filteredTypes.length > 0) {
          // Update control with fetched options and enable it
          this.polyBizEntityExplorerControl.optionsWithIcon = filteredTypes.map(entity => ({
            id: entity.id?.toString() || '',
            label: entity.name,
            icon: entity.icon || ''
          }));
          this.polyBizEntityExplorerControl.optionHeader = 'Select BizEntity Type';
          this.polyBizEntityExplorerControl.disabled = false;

          // Patch existing value when editing/viewing (use original value stored at init)
          if (this.originalBizEntityExplorerId) {
            const existingType = filteredTypes.find(t => t.id?.toString() === this.originalBizEntityExplorerId?.toString());
            if (existingType) {
              this.polyBizEntityExplorerControl.value = existingType.id?.toString() || '';
              this.polyBizEntityExplorerControl.optionSelected = existingType.name;
            }
          }
          
        } else {
          // No types available - keep disabled with appropriate message
          this.polyBizEntityExplorerControl.optionHeader = 'No BizEntity Types available';
        }
      })
      .catch(() => {
        this.isBizEntityTypesLoading = false;
        this.bizEntityTypesLoadError = true;
        // Keep control disabled on error
        this.polyBizEntityExplorerControl.optionHeader = 'Failed to load BizEntity Types';
      });
  }

  fetchBizEntityTypes(ids?: string[]): Promise<IBizEntityType[] | undefined> {
    return this.bizEntityTypeStateStoreService.getState(ids);
  }
}